import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { hasAccountTypeCookie } from '@shared/utils/cookies';
import { getConfigValue } from '@shared/utils/config';
import { pipe } from 'fp-ts/function';
import * as O from 'fp-ts/Option';

interface AccountTypeModalContextType {
  isModalOpen: boolean;
  openModal: () => void;
  closeModal: () => void;
}

const AccountTypeModalContext = createContext<AccountTypeModalContextType | undefined>(undefined);

export const AccountTypeModalProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const configValue = getConfigValue('VITE_USING_ACCOUNT_TYPES');

    const shouldShowModal = pipe(
      configValue,
      O.map(value => {
        const lowerValue = value.toLowerCase();
        return lowerValue === 'true';
      }),
      O.getOrElse(() => false),
    );

    if (shouldShowModal && !hasAccountTypeCookie()) {
      setIsModalOpen(true);
    }
  }, []);

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const openModal = () => {
    setIsModalOpen(true);
  };

  return (
    <AccountTypeModalContext.Provider value={{ isModalOpen, openModal, closeModal }}>
      {children}
    </AccountTypeModalContext.Provider>
  );
};

export const useAccountTypeModalContext = () => {
  const context = useContext(AccountTypeModalContext);
  if (context === undefined) {
    throw new Error('useAccountTypeModalContext must be used within an AccountTypeModalProvider');
  }
  return context;
};
